import { getBffBaseUrl, getVisitKey, INVITATIONS_PATH, PUBLIC_INVITATIONS_PATH } from '@/lib/bff/config'
import { readJson, sendRequest, type Role } from '@/lib/bff/people'

/*
 * BFF client for the invitation page (VIS-332, VIS-441). The token travels
 * only in a POST body, never in a URL. Before sign-in, `peek` and
 * `createAccount` are sent with the visit key and no bearer token; they say
 * nothing about an unknown or unusable invitation beyond `usable: false`
 * (peek) or 409 `invitation_not_usable` (create). After sign-in, `details`
 * and `accept` carry the bearer token and no tenant: the invitation names it.
 */

export type InvitationPeek =
  | { usable: false }
  | { usable: true; tenantName: string; maskedEmail: string; validUntil: string; timeZone: string }

export type InvitationDetails = {
  tenantName: string
  maskedEmail: string
  validUntil: string
  timeZone: string
  inviterName: string
  roles: Role[]
  emailMatches: boolean
}

export type AcceptedInvitation = { tenantId: string; tenantName: string }

/**
 * `set_password`: go to `ticketUrl`, Auth0's short-lived set-password page. It
 * is a bearer link: never render, log or record it. `sign_in`: the address
 * already has an account that can sign in.
 */
export type InvitationAccountCreation = { outcome: 'set_password'; ticketUrl: string } | { outcome: 'sign_in' }

const publicHeaders = (): HeadersInit => ({
  'x-visit-key': getVisitKey(),
  Accept: 'application/json',
  'Content-Type': 'application/json',
})

const signedInHeaders = (token: string): HeadersInit => ({ ...publicHeaders(), Authorization: `Bearer ${token}` })

const body = (token: string) => JSON.stringify({ token })

export async function peekInvitation(invitationToken: string): Promise<InvitationPeek> {
  const response = await sendRequest(`${getBffBaseUrl('mmv')}${PUBLIC_INVITATIONS_PATH}/peek`, {
    method: 'POST',
    headers: publicHeaders(),
    body: body(invitationToken),
  })
  return readJson<InvitationPeek>(response)
}

export async function createInvitationAccount(invitationToken: string): Promise<InvitationAccountCreation> {
  const response = await sendRequest(`${getBffBaseUrl('mmv')}${PUBLIC_INVITATIONS_PATH}/create-account`, {
    method: 'POST',
    headers: publicHeaders(),
    body: body(invitationToken),
  })
  return readJson<InvitationAccountCreation>(response)
}

export async function invitationDetails(accessToken: string, invitationToken: string): Promise<InvitationDetails> {
  const response = await sendRequest(`${getBffBaseUrl('mmv')}${INVITATIONS_PATH}/details`, {
    method: 'POST',
    headers: signedInHeaders(accessToken),
    body: body(invitationToken),
  })
  return readJson<InvitationDetails>(response)
}

export async function acceptInvitation(accessToken: string, invitationToken: string): Promise<AcceptedInvitation> {
  const response = await sendRequest(`${getBffBaseUrl('mmv')}${INVITATIONS_PATH}/accept`, {
    method: 'POST',
    headers: signedInHeaders(accessToken),
    body: body(invitationToken),
  })
  return readJson<AcceptedInvitation>(response)
}
